/**
 * CR305: BONUS Deal Value Model Resolver
 * 
 * Creates a unified UI value model from canonical_deal_json.value
 * for consistent rendering across V2 blocks.
 * 
 * Resolution priority:
 * 1. canonical_deal_json.value.* (preferred - nested JSON)
 * 2. Top-level deal fields (fallback)
 * 
 * CR305B: Enhanced to properly read nested canonical JSON paths
 * and provide accurate hasData flags per section.
 */

import type { Deal } from "@/lib/types/deals";
import { stripNumericPrefix, isProbablyBadBrand, getDealCardTitle } from "@/lib/deals/dealCardDisplay";
import { resolveBonusRewardBrand } from "@/lib/deals/sanitizeBrandText";

// ================================================================
// TYPES
// ================================================================

export interface BonusValueModel {
  /** Qualifying spend amount (what you pay) */
  spendAmount: number | null;
  /** Bonus/reward amount (what you get) */
  rewardAmount: number | null;
  /** Computed dollar value */
  computedValue: number | null;
  /** Effective percent back */
  effectivePercent: number | null;
  /** Cleaned title (no numeric prefixes) */
  title: string;
  /** Cleaned merchant name */
  merchantName: string | null;
  /** Cleaned reward brand name (bonus-side only, never purchase-derived) */
  rewardBrand: string | null;
  /** CR416: Source of the reward brand for diagnostics */
  rewardBrandSource: string;
  /** Labels for display */
  labels: {
    spend: string;
    reward: string;
    value: string;
  };
  /** Whether this is a valid BONUS deal with data */
  hasData: boolean;
  /** Per-section data availability flags for conditional rendering */
  sectionHasData: {
    spend: boolean;
    reward: boolean;
    value: boolean;
    dates: boolean;
  };
  /** Validity dates */
  validFrom: string | null;
  validUntil: string | null;
  dateMode: string | null;
}

// Raw canonical value object from DB
interface CanonicalValueObject {
  qualifying_spend_cad?: number | null;
  bonus_value_cad?: number | null;
  computed_value_cad?: number | null;
  effective_percent_back?: number | null;
  discount_percent?: number | null;
  pay_amount_cad?: number | null;
  receive_value_cad?: number | null;
}

// Raw canonical dates object from DB
interface CanonicalDatesObject {
  date_mode?: string | null;
  valid_from?: string | null;
  valid_until?: string | null;
}

// Extended deal type that may have canonical_deal_json
interface DealWithCanonical extends Deal {
  canonical_deal_json?: {
    value?: CanonicalValueObject;
    dates?: CanonicalDatesObject;
    identity?: {
      promo_merchant_name?: string | null;
      purchase_gift_card_brand?: string | null;
      reward_brand_name?: string | null;
    };
  } | null;
}

// ================================================================
// HELPERS
// ================================================================

/**
 * Safely extract a numeric value from nested JSON or top-level field.
 */
function extractNumber(
  nested: number | string | null | undefined,
  topLevel: number | string | null | undefined
): number | null {
  // Try nested first
  if (nested != null) {
    const num = typeof nested === 'string' ? parseFloat(nested) : nested;
    if (!isNaN(num) && num > 0) return num;
  }
  // Fall back to top-level
  if (topLevel != null) {
    const num = typeof topLevel === 'string' ? parseFloat(topLevel) : topLevel;
    if (!isNaN(num) && num > 0) return num;
  }
  return null;
}

// ================================================================
// RESOLVER
// ================================================================

/**
 * Resolve the BONUS value model from a deal.
 * 
 * Uses canonical_deal_json.value first, then falls back to top-level fields.
 * Cleans brand names and applies display helpers.
 */
export function resolveBonusValueModel(deal: DealWithCanonical): BonusValueModel {
  // Get nested canonical objects
  const canonicalValue = deal.canonical_deal_json?.value;
  const canonicalDates = deal.canonical_deal_json?.dates;
  const canonicalIdentity = deal.canonical_deal_json?.identity;
  
  // Resolve spend amount (nested > top-level)
  // Priority: canonical.value.qualifying_spend_cad > canonical.value.pay_amount_cad > top-level fields
  const spendAmount = extractNumber(
    canonicalValue?.qualifying_spend_cad ?? canonicalValue?.pay_amount_cad,
    (deal as any).qualifying_spend_cad ?? deal.spend_amount
  );
  
  // Resolve reward/bonus amount (nested > top-level)
  const rewardAmount = extractNumber(
    canonicalValue?.bonus_value_cad,
    (deal as any).bonus_value_cad ?? deal.bonus_amount ?? deal.bonus_or_discount_amount
  );
  
  // Resolve computed value (nested > top-level > calculated from reward)
  const computedValue = extractNumber(
    canonicalValue?.computed_value_cad,
    deal.computed_value_cad
  ) ?? rewardAmount; // Fall back to reward amount if no explicit computed value
  
  // Resolve effective percent back (nested > top-level > calculated)
  let effectivePercent = extractNumber(
    canonicalValue?.effective_percent_back,
    deal.effective_percent_back
  );
  
  // Calculate if not present but spend and reward are
  if (effectivePercent == null && spendAmount != null && spendAmount > 0 && rewardAmount != null) {
    effectivePercent = (rewardAmount / spendAmount) * 100;
  }
  
  // Resolve and clean title using centralized helper
  const title = getDealCardTitle(deal);
  
  // Resolve and clean merchant name
  const rawMerchant = 
    canonicalIdentity?.promo_merchant_name ??
    deal.promo_merchant_name ??
    deal.merchant ??
    null;
  const merchantName = rawMerchant && isProbablyBadBrand(rawMerchant) 
    ? stripNumericPrefix(rawMerchant) 
    : rawMerchant;
  
  // CR416: Resolve reward brand from bonus-side fields only (never purchase-side)
  const canonicalBonusBrand = canonicalIdentity?.reward_brand_name ?? null;
  const rawBonusMerchant = deal.bonus_merchant ?? null;
  const purchaseBrandRaw = 
    deal.purchase_gift_card_brand ??
    (deal as any).gift_card_brand ??
    null;
  
  const { rewardBrandDisplay, rewardBrandSource } = resolveBonusRewardBrand(
    (deal.canonical_deal_json as any)?.brands?.bonus_brand ?? null,
    rawBonusMerchant,
    canonicalBonusBrand,
    purchaseBrandRaw
  );
  
  // Resolve validity dates (nested > top-level)
  // Cast to any for top-level access since Deal type may not have these
  const dealAny = deal as any;
  const validFrom = canonicalDates?.valid_from ?? dealAny.valid_from ?? null;
  const validUntil = canonicalDates?.valid_until ?? dealAny.valid_until ?? null;
  const dateMode = canonicalDates?.date_mode ?? null;
  
  // Standard labels for BONUS deals
  const labels = {
    spend: "Qualifying Spend",
    reward: "Bonus Value",
    value: "Effective Value",
  };
  
  // Per-section data availability flags
  const sectionHasData = {
    spend: spendAmount != null && spendAmount > 0,
    reward: rewardAmount != null && rewardAmount > 0,
    value: (effectivePercent != null && effectivePercent > 0) || (computedValue != null && computedValue > 0),
    dates: validFrom != null || validUntil != null,
  };
  
  // Overall hasData = at least one value section has data
  const hasData = sectionHasData.spend || sectionHasData.reward || sectionHasData.value;
  
  return {
    spendAmount,
    rewardAmount,
    computedValue,
    effectivePercent,
    title,
    merchantName,
    rewardBrand: rewardBrandDisplay,
    rewardBrandSource,
    labels,
    hasData,
    sectionHasData,
    validFrom,
    validUntil,
    dateMode,
  };
}

/**
 * Format currency for display.
 */
export function formatCurrency(value: number | null, placeholder = "—"): string {
  if (value == null) return placeholder;
  return value >= 10 
    ? `$${Math.round(value).toLocaleString()}`
    : `$${value.toFixed(2)}`;
}

/**
 * Format percent for display.
 */
export function formatPercent(value: number | null, placeholder = "—"): string {
  if (value == null) return placeholder;
  return `${Math.round(value)}%`;
}
